/**
 * @description
 * Threshold configuration component for setting usage billing thresholds per client.
 * Allows configuration of threshold amounts, alert percentages, and auto-charge settings.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/ThresholdConfiguration.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface ThresholdConfig {
  usageThreshold: number; // in cents
  thresholdAlertAt: number; // percentage
  autoChargeOnThreshold: boolean;
}

interface ThresholdConfigurationProps {
  clientId: string;
}

/**
 * Threshold configuration component
 */
export function ThresholdConfiguration({ clientId }: ThresholdConfigurationProps) {
  const [config, setConfig] = useState<ThresholdConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Form state
  const [threshold, setThreshold] = useState(100); // in dollars
  const [alertAt, setAlertAt] = useState(80); // percentage
  const [autoCharge, setAutoCharge] = useState(true);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const config = await api.getPricingConfig(clientId);
      
      setConfig(config);
      setThreshold(config.usageThreshold / 100);
      setAlertAt(config.thresholdAlertAt);
      setAutoCharge(config.autoChargeOnThreshold);
    } catch (error) {
      console.error('Error loading threshold config:', error);
      toast.error('Failed to load threshold configuration');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const newConfig = {
        usageThreshold: threshold * 100, // Convert to cents
        thresholdAlertAt: alertAt,
        autoChargeOnThreshold: autoCharge,
      };

      const updatedConfig = await api.updatePricingConfig(clientId, newConfig);
      
      setConfig(updatedConfig);
      setEditing(false);
      toast.success('Threshold configuration saved');
    } catch (error) {
      console.error('Error saving threshold config:', error);
      toast.error('Failed to save threshold configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (config) {
      setThreshold(config.usageThreshold / 100);
      setAlertAt(config.thresholdAlertAt);
      setAutoCharge(config.autoChargeOnThreshold);
    }
    setEditing(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Usage Threshold Configuration</h3>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Edit
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Usage Threshold */}
        <div>
          <label className="text-sm font-medium">Usage Threshold</label>
          <p className="text-xs text-muted-foreground mb-2">
            Generate invoice when accumulated usage reaches this amount
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <span className="text-sm">$</span>
              <input
                type="number"
                min="10"
                max="10000"
                step="10"
                value={threshold}
                onChange={(e) => setThreshold(parseInt(e.target.value) || 100)}
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-mono">
              {formatCurrency(config?.usageThreshold || 0)}
            </div>
          )}
        </div>

        {/* Alert Threshold */}
        <div>
          <label className="text-sm font-medium">Alert Threshold</label>
          <p className="text-xs text-muted-foreground mb-2">
            Send email alert when usage reaches this percentage of threshold
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="50"
                max="95"
                value={alertAt}
                onChange={(e) => setAlertAt(parseInt(e.target.value) || 80)}
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <span className="text-sm">%</span>
            </div>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-sm font-mono">
              {config?.thresholdAlertAt || 80}%
            </div>
          )}
        </div>

        {/* Auto Charge */}
        <div>
          <label className="text-sm font-medium">Auto Charge</label>
          <p className="text-xs text-muted-foreground mb-2">
            Automatically charge invoice when threshold is reached
          </p>
          {editing ? (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={autoCharge}
                onChange={(e) => setAutoCharge(e.target.checked)}
                className="rounded border-input"
              />
              <span className="text-sm">Enable automatic charging</span>
            </label>
          ) : (
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${config?.autoChargeOnThreshold ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm">
                {config?.autoChargeOnThreshold ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="rounded-md bg-blue-50 p-3">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Configuration Preview</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• Invoice generated when usage reaches {formatCurrency(threshold * 100)}</p>
            <p>• Email alert sent at {alertAt}% of threshold ({formatCurrency(threshold * alertAt)})</p>
            <p>• Automatic charging: {autoCharge ? 'Enabled' : 'Disabled'}</p>
          </div>
        </div>

        {/* Action Buttons */}
        {editing && (
          <div className="flex gap-2 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
